import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../../store/global-selectors';
import { Pill } from 'ui-components/components/pill';
import { noop } from 'ui-components/default-props/noop';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { useProductContext } from '../product-context';
import { addTagsToProducts, removeTagsFromProducts } from '../../../../store/products/products.actions';
import { manageTags } from '../../../../store/tags/tags.actions';
import tagDropdownStyle from '../../../../components/tag-dropdown/tag-dropdown.module.less';
import style from '../../../../components/grid-header/shared-classes.module.less';
import messages from '../../messages';

const getSelectedData = (products) => {
  const selectedTagIds = new Set();
  const selectedTagsCount = {};
  products.forEach((product) => {
    const productTags = product?.tags || emptyArray;
    productTags.forEach((tag) => {
      selectedTagIds.add(tag);
      if (selectedTagsCount[tag]) {
        selectedTagsCount[tag] += 1;
      } else {
        selectedTagsCount[tag] = 1;
      }
    });
  });
  return {
    selectedTagsCount,
    selectedTagIds: [...selectedTagIds],
  };
};

const selectedDataSelector = createSelector((products) => products, getSelectedData);

const selectedTagsSelector = createSelector(
  (tags) => tags,
  (_, selectedTagIds) => selectedTagIds,
  (tags, selectedTagIds) =>
    tags.filter(
      (tag) =>
        selectedTagIds.filter((tagId) => {
          return tagId && tagId.valueOf() === tag.tagId.valueOf();
        }).length !== 0,
    ),
);

const DropDownButton = (
  <DropdownMenuToggler className={style['action-button']} theme='tertiary'>
    <FormattedMessage {...messages.tagButton} />
  </DropdownMenuToggler>
);

const TagButton = () => {
  const { selectedProductIds } = useProductContext();
  const dispatch = useDispatch();
  const addTag = useCallback(
    (productIds, tagId) => {
      dispatch(addTagsToProducts(productIds, tagId));
    },
    [dispatch],
  );
  const removeTag = useCallback(
    (productIds, tagId) => {
      dispatch(removeTagsFromProducts(productIds, tagId));
    },
    [dispatch],
  );
  const manageTagsCallback = useCallback(() => {
    dispatch(manageTags());
  }, [dispatch]);

  const tags = useSelector(Global.selectSellerTags) || emptyArray;
  const selectedProducts =
    useSelector((state) =>
      Global.selectEntitiesProductsByIds(state, {
        productIds: selectedProductIds,
      }),
    ) || emptyArray;
  const shouldBeDisabled = !selectedProductIds.length;
  const { selectedTagsCount, selectedTagIds } = selectedDataSelector(selectedProducts);
  const selectedTags = selectedTagsSelector(tags, selectedTagIds);

  const onTagToggled = useCallback(
    (tag) => {
      if (selectedTags.indexOf(tag) === -1) {
        addTag(selectedProductIds, tag.tagId);
      } else {
        removeTag(selectedProductIds, tag.tagId);
      }
    },
    [addTag, removeTag, selectedTags, selectedProductIds],
  );

  const shouldAllowDropdownToClose = useCallback((e) => {
    if (!e.target.classList || !e.target.classList.length) {
      return false;
    }
    return [...new Set(e.target.classList)].some((cssClass) => {
      return cssClass.indexOf('tag-dropdown-menu-item') !== -1;
    });
  }, []);

  return (
    <DropdownMenu
      closeWhenChildrenClicked={false}
      containerAllowedMeToClose={shouldAllowDropdownToClose}
      dropdownMenuClassName={style['dropdown-menu']}
      menuPosition='bottom-right'
    >
      {DropDownButton}
      <DropdownMenuItem className={tagDropdownStyle['tag-dropdown-menu-item']} onClick={manageTagsCallback}>
        <div>
          <FormattedMessage {...messages.manageTags} />
        </div>
      </DropdownMenuItem>
      {tags.map((tag) => (
        <DropdownMenuItem
          key={tag.tagId}
          className={style['dropdown-menu-item']}
          disabled={shouldBeDisabled}
          onClick={() => onTagToggled(tag)}
        >
          <Checkbox
            checked={selectedTags.indexOf(tag) !== -1}
            className={tagDropdownStyle.checkbox}
            disabled={shouldBeDisabled}
            indeterminate={selectedTags.indexOf(tag) !== -1 && selectedTagsCount[tag.tagId] !== selectedProductIds.length}
            onChange={noop}
          />
          <Pill className={tagDropdownStyle.pill} color={tag.hexColor} label={tag.name} theme='no-status' truncate />
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};

export default TagButton;
